/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

import { allCommands } from "app/client/components/commands";
import { GristDoc } from "app/client/components/GristDoc";
import { loadSearch } from "app/client/lib/imports";
import { isKnetEmbed } from "app/client/lib/knetEmbed";
import { makeT } from "app/client/lib/localization";
import { AppModel, reportError } from "app/client/models/AppModel";
import { DocPageModel } from "app/client/models/DocPageModel";
import { urlState } from "app/client/models/gristUrlState";
import { HomeModel } from "app/client/models/HomeModel";
import { workspaceName } from "app/client/models/WorkspaceInfo";
import { AccountWidget } from "app/client/ui/AccountWidget";
import { buildActiveUserList } from "app/client/ui/ActiveUserList";
import { buildLanguageMenu } from "app/client/ui/LanguageMenu";
import { buildNotifyMenuButton } from "app/client/ui/NotifyUI";
import { manageTeamUsersApp } from "app/client/ui/OpenUserManager";
import { UpgradeButton } from "app/client/ui/ProductUpgrades";
import { buildShareMenuButton } from "app/client/ui/ShareMenu";
import { SupportGristButton } from "app/client/ui/SupportGristButton";
import { hoverTooltip } from "app/client/ui/tooltips";
import { cssHoverCircle, cssTopBarBtn } from "app/client/ui/TopBarCss";
import { docBreadcrumbs } from "app/client/ui2018/breadcrumbs";
import { basicButton } from "app/client/ui2018/buttons";
import { cssHideForNarrowScreen, isNarrowScreenObs, testId, theme } from "app/client/ui2018/cssVars";
import { IconName } from "app/client/ui2018/IconList";
import { icon } from "app/client/ui2018/icons";
import { menu, menuDivider, menuItem, menuItemLink } from "app/client/ui2018/menus";
import { promptModal } from "app/client/ui2018/modals";
import { isFeatureEnabled } from "app/common/gristUrls";
import { showCreateOrg, showCreateWorkspace } from "app/common/knetTopBarMenus";
import { publishSharedAppearance } from "app/client/ui2018/theme";
import * as roles from "app/common/roles";
import { themeNameAppearances } from "app/common/ThemePrefs";
import { getOrgName } from "app/common/UserAPI";

import { Computed, dom, DomElementArg, makeTestId, MultiHolder, Observable, styled } from "grainjs";

import type * as searchModule from "app/client/ui2018/search";

const t = makeT("TopBar");

export function createTopBarHome(
  appModel: AppModel,
  homeModel?: HomeModel,
  onSave?: (personal: boolean) => Promise<unknown>,
) {
  const isAnonymous = !appModel.currentValidUser;

  return [
    cssFlexSpace(),
    cssButtons(
      dom.create(UpgradeButton, appModel),
      dom.create(SupportGristButton, appModel),
      (appModel.isInstallAdmin() || (appModel.isTeamSite && roles.canEditAccess(appModel.currentOrg?.access || null)) ?
        [
          basicButton(
            t("Manage team"),
            dom.on("click", () => manageTeamUsersApp({ app: appModel, onSave })),
            testId("topbar-manage-team"),
          ),
        ] :
        null
      ),
    ),
    // Inside Kue's tool workspace, Kue's shell has the org, workspace and account menus.
    isKnetEmbed() ? null : [
      homeModel ? buildWorkspaceDropdown(appModel, homeModel) : null,
      buildOrgDropdown(appModel),
      buildLanguageMenu(appModel),
      buildThemeToggleButton(appModel),
      isAnonymous ? null : buildNotifyMenuButton(appModel.notifier, appModel),
      dom("div", dom.create(AccountWidget, appModel)),
    ],
  ];
}

function buildOrgDropdown(appModel: AppModel) {
  const orgs = appModel.topAppModel.orgs;
  return cssTopBarDropBtn(
    cssDropBtnIcon("Building2"),
    cssDropBtnLabel(appModel.currentOrgName),
    cssDropChevron("Dropdown"),
    menu(() => [
      dom.forEach(orgs, org =>
        menuItemLink(
          urlState().setLinkUrl({ org: org.domain || undefined }),
          cssDropMenuRow(
            getOrgName(org),
            org.id === appModel.currentOrg?.id ? cssDropActiveTick("Tick") : null,
          ),
          testId("topbar-org-item"),
        ),
      ),
      menuDivider(),
      // Create organisation… exactly when the site switcher offers it (openspec create-from-topbar-menus).
      showCreateOrg({
        createSiteEnabled: isFeatureEnabled("createSite"),
        installAdmin: appModel.isInstallAdmin(),
        orgAdmin: appModel.isOrgAdmin(),
      }) ?
        menuItem(
          () => appModel.showNewSiteModal(),
          cssDropMenuRow(cssRefreshIcon("Plus"), t("Create organisation…")),
          testId("topbar-org-create"),
        ) :
        null,
      menuItem(
        async () => {
          try {
            // Recompute claims server-side AND refresh the session in one shot:
            // syncPermissions -> syncAndRefresh re-derives sub_permissions and writes
            // the rotated JWT back as Set-Cookie on this response, so the browser cookie
            // updates immediately (no page reload needed).
            await appModel.api.syncPermissions();
            await appModel.topAppModel.fetchUsersAndOrgs();
            // Client-side refresh intentionally disabled: the server path above already
            // refreshes the token cookie, and a second refresh here would rotate the
            // refresh token again (risking "Already Used" errors). Keep server-only for now.
            // const { refreshSession } = await import("app/client/lib/supabaseClient");
            // await refreshSession();
          }
          catch (e) {
            reportError(e as Error);
          }
        },
        cssDropMenuRow(
          cssRefreshIcon("Revert"),
          t("Refresh"),
        ),
        testId("topbar-org-refresh"),
      ),
    ]),
    testId("topbar-org-dropdown"),
  );
}

function buildWorkspaceDropdown(appModel: AppModel, homeModel: HomeModel) {
  return cssTopBarDropBtn(
    cssDropBtnIcon("Folder"),
    cssDropBtnLabel(
      // Always one workspace, never "All Documents" (openspec one-selected-workspace).
      dom.text((use) => {
        const ws = use(homeModel.selectedWS);
        return ws ? workspaceName(appModel, ws) : "";
      }),
    ),
    cssDropChevron("Dropdown"),
    menu(() => [
      dom.forEach(homeModel.workspaces, ws =>
        menuItem(
          () => urlState().pushUrl({ ws: ws.id }),
          cssDropMenuRow(
            workspaceName(appModel, ws),
            dom.domComputed(homeModel.selectedWS, selected =>
              selected?.id === ws.id ? cssDropActiveTick("Tick") : null,
            ),
          ),
          testId("topbar-ws-item"),
        ),
      ),
      menuDivider(),
      // Create workspace… for owners of the current org; the plan's workspace limit still applies
      // (openspec create-from-topbar-menus).
      showCreateWorkspace(appModel.currentOrg?.access ?? null) ?
        menuItem(
          () => promptModal(
            t("Create workspace"),
            async (name) => { if (name.trim()) { await homeModel.createWorkspace(name.trim()); } },
            t("Create"),
            "",
            t("Workspace name"),
          ),
          cssDropMenuRow(cssRefreshIcon("Plus"), t("Create workspace…")),
          testId("topbar-ws-create"),
        ) :
        null,
      menuItem(
        async () => {
          try {
            // Server-side recompute + cookie refresh (see org refresh above).
            await appModel.api.syncPermissions();
            await homeModel.updateWorkspaces();
            // Client-side refresh intentionally disabled (server path handles it).
            // const { refreshSession } = await import("app/client/lib/supabaseClient");
            // await refreshSession();
          }
          catch (e) {
            reportError(e as Error);
          }
        },
        cssDropMenuRow(
          cssRefreshIcon("Revert"),
          t("Refresh"),
        ),
        testId("topbar-ws-refresh"),
      ),
    ]),
    testId("topbar-ws-dropdown"),
  );
}

function buildThemeToggleButton(appModel: AppModel) {
  const themePrefs = appModel.themePrefs;
  const isDark = Computed.create(null, themePrefs, (_use, prefs) => {
    return prefs.colors.light === "GristDark";
  });
  return cssHoverCircle(
    dom.autoDispose(isDark),
    dom.domComputed(isDark, dark =>
      cssThemeToggleBtn(
        dark ? "Sun" : "Moon",
        hoverTooltip(() => isDark.get() ? t("Switch to Light Mode") : t("Switch to Dark Mode"),
          { key: "topBarBtnTooltip" }),
        testId("theme-toggle"),
      ),
    ),
    dom.on("click", () => {
      const prefs = themePrefs.get();
      const next = prefs.colors.light === "GristDark" ? "GristLight" : "GristDark";
      themePrefs.set({
        ...prefs,
        syncWithOS: false,
        appearance: themeNameAppearances[next],
        colors: { light: next, dark: next },
      });
      publishSharedAppearance(themeNameAppearances[next]);
    }),
  );
}

export function createTopBarDoc(owner: MultiHolder, appModel: AppModel, pageModel: DocPageModel) {
  const doc = pageModel.currentDoc;
  const renameDoc = (val: string) => pageModel.renameDoc(val);
  const displayNameWs = Computed.create(owner, pageModel.currentWorkspace,
    (use, ws) => ws ? { ...ws, name: workspaceName(appModel, ws) } : ws);

  const moduleObs = Observable.create<typeof searchModule | null>(owner, null);
  loadSearch().then(module => moduleObs.set(module)).catch(reportError);

  // Observable to decide whether to include the searchBar into this page. It doesn't work on
  // 'code' and 'acl' pages, so it's better to omit it, and let the browser's native search work.
  const enabledObs = Computed.create(owner, pageModel.gristDoc, (use, gristDoc) => {
    const viewId = gristDoc ? use(gristDoc.activeViewId) : null;
    return viewId !== null && viewId !== "code" && viewId !== "acl";
  });

  const searchModelObs = Computed.create(owner,
    moduleObs, pageModel.gristDoc, enabledObs,
    (use, module, gristDoc, enabled) => {
      if (!module || !gristDoc || !enabled) {
        return null;
      }
      return module.SearchModelImpl.create(use.owner, gristDoc);
    });

  const isSearchOpen = Computed.create(owner, searchModelObs, (use, searchModel) => {
    return Boolean(searchModel && use(searchModel.isOpen));
  });

  const isUndoRedoAvailable = Computed.create(owner, (use) => {
    const gristDoc = use(pageModel.gristDoc);
    if (!gristDoc) { return false; }

    const undoStack = gristDoc.getUndoStack();
    return !use(undoStack.isDisabled);
  });

  const isAnonymous = !pageModel.appModel.currentValidUser;

  return [
    // TODO Before gristDoc is loaded, we could show doc-name without the page. For now, we delay
    // showing of breadcrumbs until gristDoc is loaded.
    dom.maybe(pageModel.gristDoc, gristDoc =>
      cssBreadcrumbContainer(
        docBreadcrumbs(displayNameWs, pageModel.currentDocTitle, gristDoc.currentPageName, {
          docNameSave: renameDoc,
          pageNameSave: getRenamePageFn(gristDoc),
          cancelRecoveryMode: getCancelRecoveryModeFn(gristDoc),
          isPageNameReadOnly: use => use(gristDoc.isReadonly) || typeof use(gristDoc.activeViewId) !== "number",
          isDocNameReadOnly: use => use(gristDoc.isReadonly) || use(pageModel.isFork),
          isFork: pageModel.isFork,
          isBareFork: pageModel.isBareFork,
          isRecoveryMode: pageModel.isRecoveryMode,
          isTutorialFork: pageModel.isTutorialFork,
          isFiddle: Computed.create(owner, use => use(pageModel.isPrefork)),
          isSnapshot: pageModel.isSnapshot,
          isPublic: Computed.create(owner, doc, (use, _doc) => Boolean(_doc?.public)),
          isTemplate: pageModel.isTemplate,
          isAnonymous,
          isProposable: Computed.create(
            owner, gristDoc.docPageModel.currentDoc,
            (_use, currentDoc) => Boolean(currentDoc?.options?.proposedChanges?.acceptProposals),
          ),
          isReadonly: pageModel.isReadonly,
          proposeChanges: async () => {
            const { urlId } = await gristDoc.docComm.fork();
            await urlState().pushUrl({ doc: urlId });
          },
        }),
        dom.hide(use => use(isSearchOpen) && use(isNarrowScreenObs())),
      ),
    ),
    cssFlexSpace(),
    dom.maybe(pageModel.gristDoc, gristDoc => buildActiveUserList(owner, gristDoc.userPresenceModel)),
    // Don't show useless undo/redo buttons for sample docs, to leave more space for "Make copy".
    dom.maybe(pageModel.undoState, state => [
      topBarUndoBtn("Undo",
        dom.on("click", () => state.isUndoDisabled.get() || allCommands.undo.run()),
        dom.hide(use => use(isSearchOpen)),
        hoverTooltip("Undo", { key: "topBarBtnTooltip" }),
        cssHoverCircle.cls("-disabled", use => use(state.isUndoDisabled) || !use(isUndoRedoAvailable)),
        testId("undo"),
      ),
      topBarUndoBtn("Redo",
        dom.on("click", () => state.isRedoDisabled.get() || allCommands.redo.run()),
        dom.hide(use => use(isSearchOpen)),
        hoverTooltip("Redo", { key: "topBarBtnTooltip" }),
        cssHoverCircle.cls("-disabled", use => use(state.isRedoDisabled) || !use(isUndoRedoAvailable)),
        testId("redo"),
      ),
      cssSpacer(),
    ]),
    dom.domComputed((use) => {
      const model = use(searchModelObs);
      return model && use(moduleObs)?.searchBar(
        model, makeTestId("test-tb-search-"),
        pageModel.gristDoc.get()?.regionFocusSwitcher,
      );
    }),
    dom.maybe(use => !(use(pageModel.isTemplate) && isAnonymous), () => [
      buildShareMenuButton(pageModel),
      dom.maybe(pageModel.gristDoc,
        gristDoc => buildShowDiscussionButton(gristDoc)),
      dom.update(
        buildNotifyMenuButton(appModel.notifier, appModel),
        cssHideForNarrowScreen.cls(""),
      ),
    ]),
    isKnetEmbed() ? null : dom("div", dom.create(AccountWidget, appModel, pageModel)),
  ];
}

function buildShowDiscussionButton(gristDoc: GristDoc) {
  return cssHoverCircle({ style: `margin: 5px; position: relative;` },
    cssTopBarBtn("Chat", dom.cls("tour-share-icon")),
    hoverTooltip("Comments", { key: "topBarBtnTooltip" }),
    testId("open-discussion"),
    dom.on("click", () => {
      gristDoc.showTool("discussion");
      allCommands.rightPanelOpen.run();
    }),
  );
}

// Given the GristDoc instance, returns a rename function for the current active page.
// If the current page is not able to be renamed or the new name is invalid, the function is a noop.
function getRenamePageFn(gristDoc: GristDoc): (val: string) => Promise<void> {
  return async (val: string) => {
    const views = gristDoc.docModel.views;
    const viewId = gristDoc.activeViewId.get();
    if (typeof viewId === "number" && val.length > 0) {
      const name = views.rowModels[viewId].name;
      await name.saveOnly(val);
    }
  };
}

function getCancelRecoveryModeFn(gristDoc: GristDoc): () => Promise<void> {
  return async () => {
    await gristDoc.app.topAppModel.api.getDocAPI(gristDoc.docPageModel.currentDocId.get()!)
      .recover(false);
  };
}

function topBarUndoBtn(iconName: IconName, ...domArgs: DomElementArg[]): Element {
  return cssHoverCircle(
    cssTopBarUndoBtn(iconName),
    ...domArgs,
  );
}

const cssButtons = styled("div", `
  display: flex;
  gap: 8px;
  margin-right: 8px;
`);

const cssTopBarUndoBtn = styled(cssTopBarBtn, `
  background-color: ${theme.topBarButtonSecondaryFg};

  .${cssHoverCircle.className}:hover & {
    background-color: ${theme.topBarButtonPrimaryFg};
  }

  .${cssHoverCircle.className}-disabled:hover & {
    background-color: ${theme.topBarButtonDisabledFg};
    cursor: default;
  }
`);

const cssBreadcrumbContainer = styled("div", `
  padding: 7px;
  flex: 1 1 auto;
  min-width: 24px;
  overflow: hidden;
`);

const cssFlexSpace = styled("div", `
  flex: 1 1 0px;
`);

const cssSpacer = styled("div", `
  max-width: 10px;
  flex: auto;
`);

const cssThemeToggleBtn = styled(cssTopBarBtn, `
  background-color: ${theme.topBarButtonPrimaryFg};
`);

const cssTopBarDropBtn = styled("div", `
  display: flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px;
  margin: 0 2px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 13px;
  color: ${theme.topBarButtonSecondaryFg};
  white-space: nowrap;
  &:hover {
    background-color: ${theme.hover};
  }
`);

const cssDropBtnIcon = styled(icon, `
  flex: none;
  width: 14px;
  height: 14px;
  background-color: ${theme.topBarButtonSecondaryFg};
  opacity: 0.8;
`);

const cssDropBtnLabel = styled("div", `
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`);

const cssDropChevron = styled(icon, `
  flex: none;
  width: 14px;
  height: 14px;
  background-color: ${theme.topBarButtonSecondaryFg};
  opacity: 0.5;
`);

const cssDropMenuRow = styled("div", `
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
`);

const cssDropActiveTick = styled(icon, `
  flex: none;
  width: 16px;
  height: 16px;
  background-color: ${theme.accentIcon};
`);

const cssRefreshIcon = styled(icon, `
  flex: none;
  width: 14px;
  height: 14px;
  margin-right: 4px;
`);
